import { Box, Group, Menu, Paper, Stack, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { formatHumanName } from '@medplum/core';
import { ResourceAvatar } from '@medplum/react';
import { IconAlertCircle, IconCheck, IconChevronDown, IconLink } from '@tabler/icons-react';
import { ReactNode, useEffect, useState } from 'react';
import { BaseTimelineItem } from '../../types';
import { DateTimeTooltip } from '../DateTimeTooltip';
import { HighlightableItem } from '../HighlightableItem';
import { CARD_MENU_ICON_COLOR, CardMenu } from '../CardMenu';
import { ORBIT_GRAY, ORBIT_NEEDS_ACTION, ORBIT_SETTLED } from '@/theme/colors';

const BORDER = '1px solid var(--mantine-color-gray-2)';

export type CallCardPreview =
  | {
      outcome?: string;
      /** The line after the outcome, e.g. "High likelihood · Pricing". Omitted by scripts that collapse to the outcome alone. */
      detail?: string;
      /** Drives the check glyph; everything else takes the warning glyph. */
      positive: boolean;
      line?: undefined;
    }
  /** One plain status-first line: no glyph, no bold, no gray tail (the PN consultation, BAR-2925). */
  | { line: string };

export interface CallCardLabels {
  /** Expand chip on an untouched draft. */
  start: string;
  /** Expand chip on a saved note. */
  show: string;
  hide: string;
}

interface PatientAccessCallTimelineItemProps {
  title: string;
  /** `Communication/<id>` — the focus target and the copy-link value. */
  reference: string;
  timestamp: Date;
  sender: BaseTimelineItem['sender'];
  isDraft: boolean;
  /** True once the rep has recorded anything; a worked draft starts open. */
  worked: boolean;
  preview: CallCardPreview;
  labels: CallCardLabels;
  highlightRef?: string | null;
  copyDirectLink: (itemReference: string) => Promise<void>;
  /**
   * The script card, given the callback that collapses the row on save. With a
   * `glance`, also whether the note is saved, which flips the moment Save to
   * chart succeeds, before the timeline re-reads the note.
   */
  renderCard: (onSaved: () => void, charted?: boolean) => ReactNode;
  /** Menu items above Copy link, e.g. the PN card v2.0's Hide script. */
  menuItems?: ReactNode;
  /**
   * The saved card's glance row, which puts the row on the timeline card canon
   * (the PN card v2.0, BAR-2996). A draft is then always open, with no glance
   * row and no open button; a saved card closes to the glance row, and the row
   * or the round button on the card's bottom edge opens it. `preview` and
   * `labels` go unused.
   */
  glance?: ReactNode;
}

/**
 * The timeline row every Patient Access call card shares — intro call, warm
 * hand-off, PN consultation.
 *
 * Three states. An unworked draft collapses to a single line so a card created
 * before the call does not clutter the timeline. A worked draft is the full
 * script, autosaving. A saved note collapses to its outcome, with the full
 * script one click away. The card body and the preview are the script's
 * business; this owns the header, the collapsed line, focus-open and the chip.
 *
 * The header and the expand chip run edge to edge; the body sits inside them,
 * inset one step further than the title (`lg` against the header's `sm`), so
 * the card title reads as the card title above six bold section rows.
 */
export function PatientAccessCallTimelineItem({
  title,
  reference,
  timestamp,
  sender,
  isDraft,
  worked,
  preview,
  labels,
  highlightRef,
  copyDirectLink,
  renderCard,
  menuItems,
  glance,
}: PatientAccessCallTimelineItemProps) {
  // A draft nobody has touched starts closed; one already in progress stays open.
  const [expanded, setExpanded] = useState(isDraft && worked);
  // Saved here and now, before the timeline has re-read the note as charted.
  const [savedHere, setSavedHere] = useState(false);

  // Starting the card from the appointment's Chart row or the Add menu focuses
  // it here. Opening on focus is what makes that one click land on the script —
  // an effect rather than initial state because the card can mount either side
  // of the param being set.
  useEffect(() => {
    if (highlightRef && highlightRef === reference) {
      setExpanded(true);
    }
  }, [highlightRef, reference]);

  const senderName =
    sender && sender.resourceType !== 'Organization'
      ? formatHumanName(sender.name?.[0])
      : ((sender as { name?: string } | undefined)?.name ?? 'Unknown');

  const header = (bordered: boolean) => (
    <Group justify="space-between" wrap="nowrap" px="sm" py={8} style={bordered ? { borderBottom: BORDER } : undefined}>
      <Text size="sm" fw={600}>
        {title}
      </Text>
      {/* Canon header cluster: overflow -> avatar -> time. */}
      <Group gap="xs" wrap="nowrap">
        <CardMenu aria-label={`More ${title} actions`}>
          {menuItems}
          <Menu.Item
            leftSection={<IconLink size={18} color={CARD_MENU_ICON_COLOR} />}
            onClick={() => copyDirectLink(reference).catch(() => undefined)}
          >
            Copy link
          </Menu.Item>
        </CardMenu>
        {/* The name belongs in the avatar's tooltip, not spelled out in the header. */}
        <Tooltip withArrow label={senderName}>
          <ResourceAvatar value={sender} size="sm" radius="xl" />
        </Tooltip>
        <DateTimeTooltip date={timestamp} />
      </Group>
    </Group>
  );

  if (glance !== undefined) {
    const charted = !isDraft || savedHere;
    const onSaved = () => {
      setSavedHere(true);
      setExpanded(false);
    };
    if (!charted) {
      return (
        <HighlightableItem itemRef={reference} highlightRef={highlightRef}>
          <Paper withBorder p={0} radius="lg">
            {header(true)}
            <Box px="lg" pt="sm">
              {renderCard(onSaved, false)}
            </Box>
          </Paper>
        </HighlightableItem>
      );
    }
    const toggle = () => setExpanded((open) => !open);
    return (
      <HighlightableItem itemRef={reference} highlightRef={highlightRef}>
        {/* Room below for the open button, which sits half outside the card. */}
        <Paper withBorder p={0} radius="lg" mb={11} pos="relative">
          {/* The header's rule only while open; closed, the glance row sits right under it. */}
          {header(expanded)}
          <UnstyledButton
            w="100%"
            px="lg"
            pt={8}
            pb={10}
            onClick={toggle}
            aria-expanded={expanded}
            style={expanded ? { borderBottom: BORDER } : undefined}
          >
            {glance}
          </UnstyledButton>
          {expanded && <Box px="lg">{renderCard(onSaved, true)}</Box>}
          <UnstyledButton
            onClick={toggle}
            aria-label={expanded ? `Close ${title}` : `Open ${title}`}
            data-testid="pn-card-open-button"
            style={{
              position: 'absolute',
              left: '50%',
              bottom: -11,
              transform: 'translateX(-50%)',
              width: 22,
              height: 22,
              borderRadius: '50%',
              background: '#fff',
              border: '1px solid var(--mantine-color-gray-3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
            }}
          >
            <IconChevronDown size={12} color={ORBIT_GRAY} style={{ transform: expanded ? 'rotate(180deg)' : 'none' }} />
          </UnstyledButton>
        </Paper>
      </HighlightableItem>
    );
  }

  return (
    <HighlightableItem itemRef={reference} highlightRef={highlightRef}>
      <Paper withBorder p={0} radius="md">
        <Stack gap={0} w="100%">
          {header(true)}

          <Box px="lg" pt="sm">
            {!expanded && preview.line !== undefined && (
              <Text size="sm" pb="sm" truncate>
                {preview.line}
              </Text>
            )}
            {!expanded && preview.line === undefined && (
              <Group gap={6} pb="sm" wrap="nowrap">
                {preview.outcome ? (
                  <>
                    {preview.positive ? (
                      <IconCheck size={14} color={ORBIT_SETTLED} style={{ flexShrink: 0 }} />
                    ) : (
                      <IconAlertCircle size={14} color={ORBIT_NEEDS_ACTION} style={{ flexShrink: 0 }} />
                    )}
                    <Text size="sm" fw={600}>
                      {preview.outcome}
                    </Text>
                    {preview.detail && (
                      <Text size="sm" c="dimmed" truncate>
                        · {preview.detail}
                      </Text>
                    )}
                  </>
                ) : (
                  <Text size="sm" c="dimmed">
                    {isDraft ? 'Not started' : 'No outcome recorded'}
                  </Text>
                )}
              </Group>
            )}

            {expanded && renderCard(() => setExpanded(false))}
          </Box>

          <Box px="sm" py={4} style={{ borderTop: BORDER }}>
            <UnstyledButton w="100%" onClick={() => setExpanded(!expanded)}>
              <Group gap={6} justify="center">
                <IconChevronDown
                  size={13}
                  style={{ transition: 'transform 150ms ease', transform: expanded ? 'rotate(180deg)' : 'none' }}
                />
                <Text size="xs" c="dimmed">
                  {expanded ? labels.hide : isDraft ? labels.start : labels.show}
                </Text>
              </Group>
            </UnstyledButton>
          </Box>
        </Stack>
      </Paper>
    </HighlightableItem>
  );
}
